// src/rms/pages/Feedback.jsx
// ============================================================================
// Report a bug · Recommend a feature
// ============================================================================
// One component, two intents. A bug report and a feature request are the same
// act — an operator telling us something — and the only real differences are
// the question asked and whether severity means anything. Two screens would
// have meant two forms to keep in step and two sets of strings to translate
// thirteen times.
//
// Both are ordinary pages: the ad rail renders beside them like everywhere
// else. That is deliberate. Suppressing ads on the screens an operator visits
// when they are already annoyed would be the wrong instinct twice over — the
// rail is the revenue model, and a bug report is not a transaction to protect.
// ============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import feedbackService, { SEVERITIES } from "../data/feedbackService";
import { PageHeader, Field, TextInput, Select, Banner, StatusPill, formatDate } from "../components/ui";
import adEngine from "../ads/adEngine";

function FeedbackPage({ kind }) {
  const { operator, t, invalidate } = useRms();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [area, setArea] = useState("");
  const [severity, setSeverity] = useState("annoying");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [sent, setSent] = useState(null);
  const [mine, setMine] = useState([]);
  const [showContext, setShowContext] = useState(false);

  const isBug = kind === "bug";

  const context = useMemo(() => feedbackService.captureContext(operator), [operator]);

  const load = useMemo(
    () => () => {
      feedbackService.listFeedback(operator, kind).then((r) => setMine(r.data || []));
    },
    [operator, kind]
  );
  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    adEngine.recordAction();

    const result = await feedbackService.submitFeedback(operator, {
      kind, title, body, area, severity, context,
    });

    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    setSent(result.data);
    setTitle("");
    setBody("");
    setArea("");
    setSeverity("annoying");
    load();
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={isBug
          ? t("nav.reportBug", "Report a bug")
          : t("nav.recommendFeature", "Recommend a feature")}
        subtitle={isBug
          ? t("feedback.bug.subtitle", "Something not working? Tell us and we will fix it.")
          : t("feedback.feature.subtitle", "Something missing? Tell us what it would let you do.")}
      />

      <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
        {isBug ? (
          <>
            <p style={{ fontSize: 14, marginTop: 0 }}>
              {t(
                "feedback.bug.intro",
                "This software went live very recently, and the things that break first are the ones nobody testing it would ever have tried. You are running a real business on it, which makes you the only person who can find them."
              )}
            </p>
            <p style={{ fontSize: 14, color: "var(--rms-muted-strong)" }}>
              {t(
                "feedback.bug.intro2",
                "Report anything that looks wrong, however small. A wrong label costs us a minute to fix and costs you every time you read it. There is no such thing as a report that is too minor."
              )}
            </p>
          </>
        ) : (
          <>
            <p style={{ fontSize: 14, marginTop: 0 }}>
              {t(
                "feedback.feature.intro",
                "You know your trade better than we do. If something about the way you work has no place in this software yet, tell us — the whole design exists to fit twelve different trades, and it only does that when operators say what is missing."
              )}
            </p>
            <Banner tone="ok">
              {t(
                "feedback.feature.reward",
                "If we build what you suggest, we turn advertising off on your account for three months — free, automatically, no plan change needed."
              )}
            </Banner>
            <p style={{ fontSize: 13, color: "var(--rms-muted-strong)" }}>
              {t(
                "feedback.feature.rewardTerms",
                "We decide what gets built, and we will tell you either way. If two people suggest the same thing, the first one gets it. Ads come back after the three months unless you have moved to the paid tier by then."
              )}
            </p>
          </>
        )}
      </section>

      {sent ? (
        <Banner tone="ok">
          {isBug
            ? t("feedback.bug.thanks", "Thank you — that is logged. You can see it below.")
            : t("feedback.feature.thanks", "Thank you — that is logged. We will come back to you either way.")}
        </Banner>
      ) : null}

      <section className="rms-card rms-card-pad" style={{ marginBottom: 16 }}>
        <h2 className="rms-section-title">
          {isBug ? t("feedback.bug.formTitle", "What went wrong") : t("feedback.feature.formTitle", "What would you like")}
        </h2>

        {problem ? <Banner tone="danger">{problem}</Banner> : null}

        <Field
          label={t("feedback.title", "One-line summary")}
          required
          help={isBug
            ? t("feedback.titleHelpBug", "As you would say it out loud — \"the timeline shows yesterday twice\".")
            : t("feedback.titleHelpFeature", "As you would say it out loud — \"let me text a customer their agreement\".")}
        >
          <TextInput value={title} onChange={setTitle} />
        </Field>

        <Field
          label={isBug ? t("feedback.bug.body", "What happened") : t("feedback.feature.body", "What would it let you do?")}
          required
          help={isBug
            ? t(
                "feedback.bug.bodyHelp",
                "What you did, what you expected, and what happened instead. If it happens every time, say so — and if it only happened once, say that too."
              )
            : t(
                "feedback.feature.bodyHelp",
                "Describe the job you are trying to get done rather than the button you imagine. Knowing the problem usually produces a better answer than the one either of us would have guessed."
              )}
        >
          <textarea
            className="rms-textarea"
            rows={7}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>

        <div className="rms-field-grid">
          <Field
            label={t("feedback.area", "Where in the app?")}
            help={t("feedback.areaHelp", "In your own words — \"the checkout screen\", \"the invoice list\".")}
          >
            <TextInput value={area} onChange={setArea} />
          </Field>

          {isBug ? (
            <Field label={t("feedback.severity", "How bad is it?")}>
              <Select
                value={severity}
                onChange={setSeverity}
                options={SEVERITIES.map((s) => ({ value: s.value, label: t(s.labelKey, s.label) }))}
              />
            </Field>
          ) : null}
        </div>

        {/* Shown, not hidden. Telemetry an operator cannot inspect is telemetry
            they are right to distrust, and there is nothing here worth hiding. */}
        <button type="button" className="rms-linkish" onClick={() => setShowContext((v) => !v)}>
          {showContext
            ? t("feedback.context.hide", "Hide what is sent with this")
            : t("feedback.context.show", "See what is sent with this")}
        </button>
        {showContext ? (
          <div className="rms-feedback-context">
            <p style={{ marginTop: 0 }}>
              {t(
                "feedback.context.explain",
                "Attached automatically so we can reproduce it. No customer names, no bookings, nothing you have typed into the app."
              )}
            </p>
            <dl>
              {Object.entries(context).map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{value === null || value === "" ? "—" : String(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        <div style={{ marginTop: 16 }}>
          <button type="button" className="rms-btn rms-btn--primary" onClick={submit} disabled={busy}>
            {isBug ? t("feedback.bug.send", "Send the report") : t("feedback.feature.send", "Send the suggestion")}
          </button>
        </div>
      </section>

      <section className="rms-card rms-card-pad">
        <h2 className="rms-section-title">
          {isBug ? t("feedback.bug.mine", "What you have reported") : t("feedback.feature.mine", "What you have suggested")}
        </h2>

        {!mine.length ? (
          <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", margin: 0 }}>
            {t("feedback.none", "Nothing yet.")}
          </p>
        ) : (
          <div className="rms-table-wrap">
            <table className="rms-table">
              <thead>
                <tr>
                  <th>{t("feedback.title", "One-line summary")}</th>
                  <th>{t("feedback.sent", "Sent")}</th>
                  <th>{t("feedback.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {mine.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <strong>{f.title}</strong>
                      {f.response ? (
                        <div style={{ fontSize: 12.5, color: "var(--rms-muted-strong)", marginTop: 2 }}>
                          {f.response}
                        </div>
                      ) : null}
                      {f.reward_months ? (
                        <div style={{ fontSize: 12.5, color: "var(--rms-ok)", marginTop: 2 }}>
                          {t("feedback.rewarded", "{count} months ad-free granted", { count: f.reward_months })}
                        </div>
                      ) : null}
                    </td>
                    <td>{formatDate(f.created_at)}</td>
                    <td><StatusPill status={f.status} label={t(`feedback.status.${f.status}`, f.status)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export function ReportBug() {
  return <FeedbackPage kind="bug" />;
}

export function RecommendFeature() {
  return <FeedbackPage kind="feature" />;
}

export default FeedbackPage;
